import {
  fetchVoiceNavSessionContext,
  postVoiceNavSessionActivePatient,
  postVoiceNavSessionPendingUiReplay,
  type VoiceNavSessionActivePatientPayload,
} from '../api/apiConfig'
import type { PatientContext } from '../types/widget'
import { consumePendingModalityReplayAfterReload } from '../utils/pendingModalityAfterReload'
import { activePatientPayloadFromHostPatientContext } from '../domain/patientHost/activePatientFromChatCore'
import type { HostPatientNavigationAdapter } from './HostPatientNavigationAdapter'
import type {
  ActivePatientSyncPayload,
  DeferredReplaySessionContext,
  PendingUiReplayResponse,
} from '../domain/patientHost/types'

export type HttpVoiceNavHostAdapterParams = {
  apiBaseUrl: string
  getCredentials: () => {
    piBearerToken: string
    loggedUserId: string
    medCenterId?: string
  }
  getPatientContextFromHost: () => PatientContext | Record<string, unknown> | undefined
}

/**
 * Production adapter: Redis-backed session + replay via existing VoicNav HTTP routes.
 * Laravel (or any host) only needs to embed the widget with credentials + ``patientContext``;
 * no Laravel types appear here.
 */
export function createHttpVoiceNavHostAdapter(
  params: HttpVoiceNavHostAdapterParams,
): HostPatientNavigationAdapter {
  const toWirePayload = (p: ActivePatientSyncPayload): VoiceNavSessionActivePatientPayload => ({
    ...(p.patient_id ? { patient_id: p.patient_id } : {}),
    ...(p.patient_name ? { patient_name: p.patient_name } : {}),
    ...(p.dob ? { dob: p.dob } : {}),
    ...(p.patient_unique_id ? { patient_unique_id: p.patient_unique_id } : {}),
    ...(p.patient_fb_id ? { patient_fb_id: p.patient_fb_id } : {}),
  })

  return {
    async getHostActivePatient() {
      return activePatientPayloadFromHostPatientContext(params.getPatientContextFromHost())
    },
    async fetchPersistedSessionContext(): Promise<DeferredReplaySessionContext | null> {
      const creds = params.getCredentials()
      if (!creds.piBearerToken.trim() || !creds.loggedUserId.trim()) return null
      const ctx = await fetchVoiceNavSessionContext(params.apiBaseUrl, creds)
      return ctx as DeferredReplaySessionContext | null
    },
    async persistActivePatientForReplay(payload: ActivePatientSyncPayload): Promise<boolean> {
      const creds = params.getCredentials()
      if (!creds.piBearerToken.trim() || !creds.loggedUserId.trim()) return false
      const ap = await postVoiceNavSessionActivePatient(
        params.apiBaseUrl,
        creds,
        toWirePayload(payload),
      )
      return ap != null
    },
    async requestPendingUiReplay(): Promise<PendingUiReplayResponse | null> {
      const creds = params.getCredentials()
      if (!creds.piBearerToken.trim() || !creds.loggedUserId.trim()) return null
      return postVoiceNavSessionPendingUiReplay(params.apiBaseUrl, creds)
    },
    consumeBrowserDeferredModalityHint(): boolean {
      return consumePendingModalityReplayAfterReload()
    },
  }
}
